import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { useApp } from '@/store';
import { latestPartsFor } from '@/lib/device-parts';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { exportPdf } from '@/lib/pdf';
import { ResponsiveGrid } from '@/components/layout';

export function ExtinguisherReport() {
  const t = useT();
  const { id } = useParams();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const settings = useApp(s => s.settings);
  const deviceTypes = useApp(s => s.deviceTypes);
  const spareParts = useApp(s => s.spareParts);
  const inspections = useApp(s => s.inspections);
  const printRef = useRef<HTMLDivElement>(null);

  if (!customer) return <div>{t('common.notFound')}</div>;

  const typeName = (typeId?: string) =>
    deviceTypes.find(dt => dt.id === typeId)?.nameDe ?? typeId ?? '';
  const partName = (a: string) =>
    spareParts.find(p => p.articleNr === a)?.name ?? a;
  // Parts live on the inspection, not on the device — see lib/device-parts.
  const customerInspections = inspections.filter(
    i => i.customerId === customer.id
  );
  const partsFor = (deviceId: string) =>
    latestPartsFor(deviceId, customerInspections);

  const handlePdf = () => {
    if (printRef.current) {
      exportPdf(printRef.current, `Extinguisher-Report-${customer.id}.pdf`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="no-print flex justify-between items-center">
        <Link
          to={`/customer/${customer.id}/extinguishers`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            {t('common.print')}
          </Button>
          <Button size="sm" onClick={handlePdf}>
            <Download className="h-4 w-4" />
            {t('common.pdf')}
          </Button>
        </div>
      </div>

      {/* No outer frame: this is a paper document, and the border printed as a
          box around the whole page (client #81). border-0/shadow-none rather
          than a print-only rule because exportPdf rasterises the on-screen
          element, so print styles never reach the PDF. */}
      <Card
        ref={printRef}
        className="print-page max-w-[210mm] mx-auto p-4 sm:p-8 border-0 shadow-none"
      >
        <div className="border-b pb-4 mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="font-bold text-lg">{settings.companyName}</div>
            <div className="text-xs">{settings.street}</div>
            <div className="text-xs">
              {settings.zip} {settings.city}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-primary">
              {t('extReport.title')}
            </div>
            <div className="text-xs mt-1">
              {t('hoseReport.date')}: {new Date().toISOString().slice(0, 10)}
            </div>
            <div className="text-xs">
              {t('hoseReport.customer')}:{' '}
              <span className="font-mono">{customer.id}</span>
            </div>
          </div>
        </div>

        <ResponsiveGrid cols={2} className="text-sm mb-6">
          <div>
            <div className="font-semibold mb-1">
              {t('customer.billingAddress')}
            </div>
            <div className="text-xs">
              {customer.billingAddress?.company}
              <br />
              {customer.billingAddress?.street}
              <br />
              {customer.billingAddress?.zip} {customer.billingAddress?.city}
            </div>
          </div>
          <div>
            <div className="font-semibold mb-1">
              {t('hoseReport.objectSite')}
            </div>
            <div className="text-xs">
              {customer.objectAddress?.street}
              <br />
              {customer.objectAddress?.zip} {customer.objectAddress?.city}
            </div>
          </div>
        </ResponsiveGrid>

        <div className="rounded bg-blue-50 dark:bg-blue-950/30 p-3 text-xs mb-6">
          {t('common.count')}: {customer.extinguishers.length}
        </div>

        <div className="space-y-4">
          {customer.extinguishers.map(e => (
            <div key={e.id} className="border rounded p-4 break-inside-avoid">
              <div className="mb-3">
                <div className="font-semibold text-sm">
                  <span className="font-mono text-destructive">
                    #{e.deviceNumber || e.id}
                  </span>{' '}
                  — {e.mfr} {e.model}
                </div>
                <div className="text-xs text-muted-foreground">
                  {/* Drop the separator when a field is blank, otherwise the
                      line prints as a bare "Stockwerk: ·". */}
                  {[
                    e.floor ? `${t('table.floor')}: ${e.floor}` : '',
                    e.location,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <ResponsiveGrid cols={3} className="text-xs">
                <div>
                  <div className="font-semibold mb-1">
                    {t('hoseReport.equipment')}
                  </div>
                  <div>
                    {t('ext.type')}: {typeName(e.type)}
                  </div>
                  <div>
                    {t('ext.content')}: {e.content}
                  </div>
                  <div>
                    {t('ext.year')}: {e.year}
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    {t('customer.inspections')}
                  </div>
                  <div>
                    {t('customer.lastInspection')}: {fmtDate(e.lastInspection)}
                  </div>
                  <div>
                    {t('customer.nextInspection')}: {fmtDate(e.nextInspection)}
                  </div>
                  <div>
                    {t('inspection.refilled')}: {fmtDate(e.refilled)}
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-1">
                    {t('ext.spareParts')}
                  </div>
                  {/* One part per line ran the card down the page for no
                      reason; the client asked for a comma-separated list
                      (client #81). */}
                  <div>
                    {partsFor(e.id).length === 0
                      ? '—'
                      : partsFor(e.id).map(partName).join(', ')}
                  </div>
                </div>
              </ResponsiveGrid>
            </div>
          ))}
          {customer.extinguishers.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('customer.noExtinguishers')}
            </p>
          )}
        </div>

        <div className="grid gap-8 sm:grid-cols-2 mt-12 pt-8 border-t text-xs">
          <div>
            <div className="border-b border-foreground pb-1 mb-1">&nbsp;</div>
            <div>{t('hoseReport.techSignature')}</div>
          </div>
          <div>
            <div className="border-b border-foreground pb-1 mb-1">&nbsp;</div>
            <div>{t('hoseReport.custSignature')}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
